import { useState } from "react";
import { useSession } from "@/lib/auth/session";
import { useTheme } from "@/app/providers/ThemeProvider";
import { Moon, Sun, Loader2 } from "lucide-react";
import { MixcoreMark } from "@/ui/brand/MixcoreMark";

/**
 * Login Nexus — único método: Google Sign-In via Firebase.
 *
 * Por diseño NO hay login email+password en el back-office:
 *  - Las cuentas con acceso a Nexus son del equipo Mixcore (allowlist
 *    en env `NEXUS_ALLOWED_EMAILS`).
 *  - Esas cuentas son siempre Google Workspace (gonzalo@mixcore.club…).
 *  - El JWT lo emite `/users/google-authentication` después de validar
 *    la allowlist; el frontend nunca maneja contraseñas.
 *
 * Si tu email no está en la allowlist, el backend devuelve 403 y mostramos
 * el mensaje "Acceso denegado al portal Nexus" — no creamos cuenta.
 */
export function LoginScreen() {
  const loginWithGoogle = useSession((s) => s.loginWithGoogle);
  const error = useSession((s) => s.error);
  const { theme, toggle } = useTheme();

  const [submitting, setSubmitting] = useState(false);

  async function handleLogin() {
    setSubmitting(true);
    try {
      await loginWithGoogle();
    } catch {
      /* error ya está en el store */
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-base px-4">
      <button
        onClick={toggle}
        className="fixed top-4 right-4 p-2 rounded-md text-fg-muted hover:bg-bg-hover hover:text-fg transition-colors"
        aria-label="Cambiar tema"
      >
        {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      <div className="w-full max-w-sm animate-fade-in">
        <div className="flex flex-col items-center gap-2 mb-8">
          <MixcoreMark variant="tile" size={48} className="shadow-nx-md rounded-xl" />
          <div className="text-center">
            <h1 className="text-lg font-bold text-fg tracking-tight">MIXCORE</h1>
            <p className="text-xs font-semibold text-accent uppercase tracking-widest">
              Nexus
            </p>
          </div>
        </div>

        <div className="nx-card p-6">
          <h2 className="text-base font-semibold text-fg mb-1">Acceso restringido</h2>
          <p className="text-xs text-fg-muted mb-5 leading-relaxed">
            Solo cuentas del equipo Mixcore autorizadas en la allowlist pueden
            entrar al back-office. Iniciá sesión con tu cuenta corporativa.
          </p>

          {error && (
            <div className="text-xs text-danger bg-danger-soft border border-danger/20 rounded-md px-3 py-2 mb-4">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={handleLogin}
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium bg-bg-surface border border-border text-fg hover:bg-bg-hover hover:border-border-strong transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <Loader2 size={16} className="animate-spin text-fg-muted" />
            ) : (
              <GoogleLogo />
            )}
            Continuar con Google
          </button>
        </div>

        <p className="text-2xs text-fg-subtle text-center mt-5">
          v2.0 · Si te falta acceso, contactá al Owner para que te asigne un rol.
        </p>
      </div>
    </div>
  );
}

/** Logo oficial de Google, inline (sin dependencia). */
function GoogleLogo() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}
